import React, { useMemo, useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { ConsentTerms, consentRowsFor, documentsOf } from "./ConsentTerms";
import { Button } from "../../../components/ui";
import { recordConsent } from "../services/consentService";
import { ApiError } from "../../../lib/http";
import { ROUTES } from "../../../routes/paths";
import type { ConsentRowKey } from "./ConsentTerms";
import type { GetToken } from "../types/auth.types";
import type { ConsentDocument } from "../types/consent.types";

/**
 * The consent a signed-in user still owes, asked for on the way in.
 *
 * A sign-up gives its consent before the account exists, so it never sees this.
 * This is the other case: a returning user whose agreement the backend says is
 * incomplete - because a document has been published or republished since they
 * last gave it. `RequireAuth` holds the app here until it is given, which is
 * what makes it a gate rather than a notice: the point of asking is that the
 * processing does not continue until the answer is yes.
 *
 * Only what is outstanding is asked about. A user being shown a republished
 * Privacy policy is not made to confirm their age again to get past it.
 *
 * The POST is made from here rather than handed back to the caller, so a server
 * that refuses can be answered on the screen the user is already looking at,
 * with the ticks they already made still in front of them.
 */
export const ConsentRequired: React.FC<{
  documents: ConsentDocument[];
  getToken: GetToken;
  /** Called once the backend has recorded the agreement, to release the app. */
  onRecorded: () => void;
}> = ({ documents, getToken, onRecorded }) => {
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const rows = useMemo(
    () => consentRowsFor(documents.map((document) => document.documentType)),
    [documents]
  );

  const [agreed, setAgreed] = useState<Partial<Record<ConsentRowKey, boolean>>>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const allAgreed = rows.every((row) => agreed[row.key]);

  const tick = (key: ConsentRowKey, checked: boolean) => {
    setAgreed((previous) => ({ ...previous, [key]: checked }));
    setError("");
  };

  const handleAgree = async () => {
    if (!allAgreed || submitting) return;

    setSubmitting(true);
    setError("");
    try {
      // Intersected with what the backend actually asked for: a row can stand
      // for two documents, and only one of them being outstanding is no reason
      // to re-assert the other.
      const ticked = documentsOf(rows.filter((row) => agreed[row.key]));
      await recordConsent(
        getToken,
        documents.filter((document) => ticked.includes(document.documentType))
      );
      onRecorded();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "We could not save your agreement. Please check your connection and try again."
      );
      setSubmitting(false);
      console.error("consent gate submission failed:", caught);
    }
  };

  return (
    <AuthLayout wide showFooter={false}>
      <h1 className="text-[24px] leading-tight font-semibold text-[#1F2937]">
        Before we continue
      </h1>
      <p className="mt-1 text-[14px] text-[#62636C]">
        Required under India&rsquo;s DPDP Act, 2023.
      </p>

      <h2 className="mt-6 text-[16px] font-medium text-[#1E1F24]">
        Let&rsquo;s sign below terms
      </h2>

      <ConsentTerms rows={rows} agreed={agreed} onToggle={tick} />

      {error && (
        <p className="mt-6 text-center text-xs text-red-500">{error}</p>
      )}

      <div className="mt-8 flex flex-col items-center gap-3">
        <Button
          variant="primary"
          size="lg"
          onClick={handleAgree}
          loading={submitting}
          disabled={!allAgreed}
          className="w-[380px] max-w-full"
        >
          Agree &amp; proceed
        </Button>

        {/* The way out for someone not willing to agree. Without it the only
            exits from a gate that holds the whole app are the back button and
            closing the tab, neither of which ends the session. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => signOut(() => navigate(ROUTES.login, { replace: true }))}
        >
          Log out
        </Button>
      </div>
    </AuthLayout>
  );
};
